import { useEffect } from 'react';

import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { normalizeId } from '../utils/ids';

/**
 * "These items wear cards; make sure the cache holds them." Given the items
 * a surface renders (or bare card ids), reports any card the definitions
 * cache does not hold, and the cache fetches itself — so an item of a card
 * that was added after this session loaded (by a colleague, by an API
 * client) gets its layout, its badge and its resolved references without
 * anyone reloading the page. See CardDefinitionsContext.noteMissingCards
 * for the debounce, the rate limit and the memory of ids that stay missing.
 *
 * Safe anywhere: with no provider (the public shared page, isolated tests)
 * it does nothing.
 */
export default function useEnsureCards(itemsOrIds) {
	const ctx = useOptionalCardDefinitions();
	const noteMissingCards = ctx?.noteMissingCards;
	const cardsById = ctx?.cardsById;
	const loaded = ctx?.loaded;

	useEffect(() => {
		if (!noteMissingCards || !loaded) return;
		const list = Array.isArray(itemsOrIds) ? itemsOrIds : (itemsOrIds ? [itemsOrIds] : []);
		const missing = [];
		list.forEach(entry => {
			const id = normalizeId(entry && typeof entry === 'object' ? entry.card_id : entry);
			if (id && !cardsById?.[id] && !missing.includes(id)) missing.push(id);
		});
		if (missing.length) noteMissingCards(missing);
	}, [itemsOrIds, cardsById, loaded, noteMissingCards]);
}
